/**
 * MathLive / pasted equations can hand us LaTeX that contains Mathematical
 * Alphanumeric Symbols taken from the Supplementary Multilingual Plane, for
 * example U+1D465 MATHEMATICAL ITALIC SMALL X (𝑥) or U+1D466 (𝑦).
 *
 * Those code points need 4 bytes in UTF-8. They round-trip fine through
 * `utf8mb4` columns, but MySQL/MariaDB silently rewrites them to a literal
 * question mark when the column/connection is `utf8mb3` (or `latin1`). That is
 * what leaves equations looking like `(? + 3) ^2 + (? + 8) ^2 = 81`.
 *
 * KaTeX renders the ASCII letter `x` inside a formula with exactly the same
 * italic math font used for 𝑥, so folding these characters down to their
 * plain ASCII / Basic-Multilingual-Plane form is visually lossless for the
 * student, while making the LaTeX we persist 7-bit safe.
 */

// U+1D400..U+1D7FF: Mathematical Alphanumeric Symbols (Latin, Greek, digits).
const MATH_ALPHANUMERIC_RE = /[\u{1D400}-\u{1D7FF}]/gu;

// Compatibility-decomposes a single astral math character (𝑥 -> x, 𝐀 -> A,
// 𝔸 -> A, 𝜶 -> α). Unassigned code points have no decomposition and are
// returned untouched.
const foldMathAlphanumeric = (character) => {
   const folded = character.normalize('NFKC');
   return folded === character ? character : folded;
};

/**
 * Rewrites Supplementary-Plane Mathematical Alphanumeric Symbols into their
 * Basic-Multilingual-Plane equivalent so the LaTeX survives storage in any
 * MySQL/MariaDB character set.
 *
 * @param {string} latex LaTeX source coming from the math input.
 * @returns {string} LaTeX that no longer contains astral math characters.
 */
export const normalizeMathLatex = (latex) => {
   if (typeof latex !== 'string' || latex.length === 0) {
      return typeof latex === 'string' ? latex : '';
   }

   // Guard keeps every ASCII-only formula (the common case) allocation free.
   if (!MATH_ALPHANUMERIC_RE.test(latex)) return latex;
   MATH_ALPHANUMERIC_RE.lastIndex = 0;

   return latex.replace(MATH_ALPHANUMERIC_RE, foldMathAlphanumeric);
};

export default normalizeMathLatex;
